import { useCallback, useState } from 'react';
import type { SessionRef } from '../types.ts';

// Fetch the logged-in user's chat sessions for the history drawer.
// Returns an empty list (not an error) when the user is anonymous — the
// server endpoint returns 401, which we map to a clean "no sessions" UI.
//
// No fetch-on-mount: the drawer triggers `refresh()` when opened, and the
// initial /api/sessions call was wasteful (every anonymous landing logged
// a 401 to the browser console).

type ApiSession = {
  id: string;
  title: string;
  regulation: string | null;
  kind: string | null;
  turnCount: number;
  pinned: boolean;
  archived: boolean;
  updatedAt: string;
};

function relativeFromTimestamp(iso: string): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return '';
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  return `${Math.floor(days / 30)} months ago`;
}

export function useSessions(): {
  sessions: SessionRef[];
  loading: boolean;
  refresh: () => Promise<void>;
} {
  const [sessions, setSessions] = useState<SessionRef[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/sessions');
      if (!res.ok) {
        // 401 (anonymous) or other failure — render an empty list, don't
        // shout. The drawer's own empty state handles it.
        setSessions([]);
        return;
      }
      const data = await res.json();
      const mapped: SessionRef[] = (data.sessions ?? []).map((s: ApiSession) => ({
        id: s.id,
        title: s.title,
        relative: relativeFromTimestamp(s.updatedAt),
        regulation: s.regulation ?? 'unknown',
        turns: s.turnCount,
        kind: (s.kind as SessionRef['kind']) ?? 'build',
        pinned: s.pinned,
        archived: s.archived,
      }));
      setSessions(mapped);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return { sessions, loading, refresh };
}
